<template>
  <el-container class="background-white" v-model="loading">
    <el-header>
      <h1>Orders on Hold</h1>
    </el-header>

    <el-main>
      <el-row>
        <el-col :span="12">
          <el-switch
            v-model="autoRefresh"
            :disabled="loading"
            active-text="Auto-Refresh On"
            inactive-text="Auto-Refresh Off"></el-switch>
        </el-col>
        <el-col :span="12" class="text-right">
          <el-button
            type="primary"
            round @click="fetch"
            :disabled="loading || autoRefresh">
            Refresh
          </el-button>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="8" :offset="16">
          <el-select 
            v-model="status"
            size="mini"
            placeholder="Select Status"
            @change="fetch">
            <el-option
              v-for="item in statusOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value">
            </el-option>
          </el-select>
        </el-col>
      </el-row>

      <el-row>
        <el-col :span="24">
          <el-table
            v-loading="loading"
            :data="orders"
            style="width: 100%"
            max-height="600">
            <el-table-column
              label="Customer"
              width="300">
              <template slot-scope="scope">
                <span>{{ fullName(scope.row) }}</span>
              </template>
            </el-table-column>
            <el-table-column
              prop="country"
              label="Country"
              width="100">
            </el-table-column>
            <el-table-column
              prop="region"
              label="Store"
              width="100">
            </el-table-column>
            <el-table-column
              label="Order ID"
              width="150">
              <template slot-scope="scope">
                <a :href="scope.row.shopify_url" target="_blank" style="display: block;">{{ scope.row.order_id }}</a>
              </template>
            </el-table-column>
            <el-table-column
              label="Received On"
              width="100">
              <template slot-scope="scope">
                <span>{{ displayDate(scope.row.received_on) }}</span>
              </template>
            </el-table-column>
            <el-table-column
                label="Reason"
                prop="statistics.reason">
            </el-table-column>
            <el-table-column
                label="Description"
                prop="statistics.description">
            </el-table-column>
            <el-table-column
              v-if="status === 'declined'"
              label="Declined On"
              width="120">
              <template slot-scope="scope">
                <span>{{ displayDate(scope.row.updated_at) }}</span>
              </template>
            </el-table-column>
            <el-table-column
              align="right">
              <template slot-scope="scope">
                <template v-if="status === 'pending'">
                  <el-button size="mini" @click.native.prevent="review(scope.row, scope.$index, 'approved')">Approve</el-button>
                  <el-button size="mini" @click.native.prevent="review(scope.row, scope.$index, 'declined')">Decline</el-button>
                </template>
                <template v-if="status === 'declined' && isRevertible(scope.row)">
                  <el-button size="mini" type="warning" @click.native.prevent="review(scope.row, scope.$index, 'approved')">Revert to Approved</el-button>
                </template>
              </template>
            </el-table-column>
          </el-table>
        </el-col>
      </el-row>
    </el-main>
  </el-container>
</template>

<script>
  import axios, { RepositoryError } from '../../../repositories';
  import { isRevertible } from './isRevertible';

  export default {
    name: 'KitsilanoHeldOrders',
    created() {
      this.fetch();
    },
    data() {
      return {
        autoRefresh: false,
        autoRefreshInterval: 10000,
        refreshInterval: null,
        loading: true,
        orders: [],
        status: 'pending',
        statusOptions: [
          {value: 'pending', label: 'Pending'},
          {value: 'approved', label: 'Approved'},
          {value: 'declined', label: 'Declined'},
        ],
      };
    },
    methods: {
      async fetch() {
        this.loading = true;

        try {
          const response = await axios.get('/kitsilano/orders/hold', {
            params: { status: this.status },
          });
          this.orders = response.data;
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        } finally {
          this.loading = false;
        }
      },
      isRevertible,
      async review(order, index, verdict) {
        const isRevert = this.status === 'declined' && verdict === 'approved';
        const action = isRevert ? 'revert' : (verdict === 'approved' ? 'approve' : 'decline');
        const message = isRevert
          ? 'This will revert the order to approved status and re-trigger fulfillment. Continue?'
          : `This will ${action} the order. Continue?`;

        try {
          await this.$confirm(message, 'Warning', {
            confirmButtonText: 'Yes',
            cancelButtonText: 'No',
            type: 'warning'
          });
        } catch {
          return;
        }


        this.loading = true;

        try {
          await axios.put(`/kitsilano/orders/hold/${order.id}`, {
            status: verdict,
          });

          this.orders.splice(index, 1);
        } catch (err) {
          this.$message.error(err?.message || `Could not ${action} order ${order.order_id}. Please try again later.`);
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        } finally {
          this.loading = false;
        }
      },
      fullName(holdOrder) {
        if (!holdOrder.customer_first_name) {
          return '';
        }

        if (!holdOrder.customer_last_name) {
          return holdOrder.customer_first_name;
        }

        return `${holdOrder.customer_first_name} ${holdOrder.customer_last_name}`;
      },
      displayDate(date) {
        if (!date) {
          return '';
        }

        date = new Date(date);

        return date.toLocaleDateString('en-CA');
      }
    },
    watch: {
      autoRefresh: function(val) {
        if (val) {
          return this.refreshInterval = setInterval(
            () => this.fetch(),
            this.autoRefreshInterval,
          );
        }

        clearTimeout(this.refreshInterval);
      },
    },
  };
</script>
